import type { ReactNode } from "react";
import { Badge, Card, OwnerChip, initialsOf } from "@playbookai/ui";
import type { ManagedKpiSummary } from "../api/types.js";
import type { KpiPersonName } from "../hooks/useOwnerNames.js";
import { KpiLifecyclePill } from "./KpiStatusBadges.js";
import { KpiReadinessPill } from "./KpiReadinessPill.js";
import { valueTypeLabel } from "./kpi-presentation.js";

export interface KpiCardProps {
  readonly kpi: ManagedKpiSummary;
  readonly definitionName: string;
  /** Category name resolved by the collection; null when the KPI has none. */
  readonly categoryName?: string | null;
  /** The owner as the collection resolved them; omitted while unresolved. */
  readonly owner?: KpiPersonName;
  readonly onOpen: () => void;
  /** The row's overflow menu, rendered in the card's top corner (d12). */
  readonly actions?: ReactNode;
}

/** Card presentation for All KPIs as the canvas draws it (PB-5134, d35): the
 * name, its value type and category, the two pills, and the owner. */
export function KpiCard({
  kpi,
  definitionName,
  categoryName = null,
  owner,
  onOpen,
  actions,
}: KpiCardProps): JSX.Element {
  // An unresolved owner is a FACT about the directory, not an identity — so
  // the fallback states that an owner exists ("Owner assigned") rather than
  // presenting a truncated id fragment as though it were a name.
  const ownerLabel = kpi.ownerUserId ? "Owner assigned" : "Unassigned";

  return (
    // Card-as-overlay (the HoshinPlanCard pattern), NOT a <button> around the
    // card: the heading's REAL <button> keeps native activation and its
    // ::after stretches over the card, so clicking anywhere still opens the
    // KPI, while the menu sits above the overlay.
    //
    // Kept hand-built (not Link stretched): `onOpen` is a plain callback —
    // the real route it resolves to (settingsKpiDetailRoute) is built in
    // apps/shell, outside this package, so no href reaches this component.
    // Link's `stretched` needs a real href/router Link (see ModuleLibraryCard
    // and Link.aschild.test.tsx: asChild always wraps the host router's own
    // Link); an anchor with no href is not keyboard-reachable. Same "plain
    // inline trigger, not a boxed control" reasoning as KpiPanel.tsx and
    // KpiListView.tsx.
    <Card
      interactive
      className="group relative flex h-full flex-col gap-2.25 px-4 py-3.75 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-pb-teal"
    >
      <div className="flex items-start justify-between gap-2.5">
        <div className="min-w-0">
          <h3 className="text-title leading-snug text-pb-text">
            <button
              type="button"
              onClick={onOpen}
              aria-label={`Open KPI: ${kpi.name}`}
              className="line-clamp-2 text-left hover:underline after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
            >
              {kpi.name}
            </button>
          </h3>
          <p
            className="mt-px text-caption text-pb-text-muted"
            title={`${definitionName} · ${kpi.unit}`}
          >
            {valueTypeLabel(kpi.unit)}
            {categoryName ? ` · ${categoryName}` : ""}
          </p>
        </div>
        {actions ? (
          <span className="relative z-10 shrink-0">{actions}</span>
        ) : null}
      </div>

      <div className="flex flex-wrap gap-1.75">
        <KpiLifecyclePill status={kpi.lifecycleStatus} />
        <KpiReadinessPill readiness={kpi.readiness} />
      </div>

      <div className="mt-auto flex items-center gap-1.75 border-t border-pb-border/50 pt-2.25 text-body-small text-pb-text-secondary">
        {owner ? (
          <>
            <OwnerChip
              initials={initialsOf(owner.name)}
              name={owner.name}
              seed={kpi.ownerUserId}
              className="min-w-0 max-w-full"
            />
            {owner.active ? null : (
              <Badge shape="pill" size="sm" tone="watch" variant="soft">
                Not active
              </Badge>
            )}
          </>
        ) : (
          <span className="truncate text-pb-text-muted">{ownerLabel}</span>
        )}
      </div>
    </Card>
  );
}
